import type { Icons } from '@clientbase/clientbase-library'

export type VariableItem = {
  label: string
  token: string
}

export type VariableCategory = {
  key: string
  label: string
  icon: Icons
  items: VariableItem[]
}

export const RECURRENCE_CATEGORY_KEY = 'recurrence'

/**
 * Tokens da categoria Recorrência. Fonte da verdade para a validação de
 * bloqueio em cobrança avulsa. Espelha o RECURRENCE_TOKENS do backend.
 */
export const RECURRENCE_TOKENS: string[] = [
  '{{itens_recorrencia}}',
  '{{valor_total_recorrencia}}',
  '{{valor_total_recorrencia_extenso}}',
  '{{frequencia_recorrencia}}',
  '{{ciclo_atual_recorrencia}}',
  '{{total_ciclos_recorrencia}}',
  '{{total_ciclos_recorrencia_extenso}}',
  '{{dia_vencimento_recorrencia}}',
  '{{forma_pagamento_recorrencia}}',
  '{{juros_recorrencia}}',
  '{{multa_recorrencia}}',
  '{{desconto_recorrencia}}',
  '{{bonificacao_recorrencia}}',
  '{{data_ultimo_ciclo_recorrencia}}'
]

// --- Empresa (labels variam PJ vs PF, token idêntico) ---
const empresaItems = (isCNPJ: boolean): VariableItem[] => {
  const items: VariableItem[] = [
    {
      label: isCNPJ ? 'Razão social' : 'Nome completo',
      token: '{{empresa_razao_social}}'
    }
  ]

  if (isCNPJ) {
    items.push({ label: 'Nome fantasia', token: '{{empresa_nome_fantasia}}' })
  }

  items.push(
    { label: isCNPJ ? 'CNPJ' : 'CPF', token: '{{empresa_documento}}' },
    { label: 'Endereço', token: '{{empresa_endereco}}' },
    { label: 'Email', token: '{{empresa_email}}' },
    { label: 'Telefone', token: '{{empresa_telefone}}' },
    {
      label: isCNPJ ? 'Data de fundação' : 'Data de nascimento',
      token: '{{empresa_data_fundacao}}'
    }
  )

  return items
}

// --- Cliente ---
const clienteItems: VariableItem[] = [
  { label: 'Nome completo do cliente', token: '{{nome_cliente}}' },
  { label: 'Apelido do cliente', token: '{{apelido_cliente}}' },
  { label: 'Documento do cliente', token: '{{documento_cliente}}' },
  { label: 'Endereço do cliente', token: '{{endereco_cliente}}' },
  { label: 'Email do cliente', token: '{{email_cliente}}' },
  { label: 'Telefone do cliente', token: '{{telefone_cliente}}' }
]

// --- Gerais ---
const geraisItems: VariableItem[] = [
  { label: 'Data atual', token: '{{data_atual}}' },
  { label: 'Data atual por extenso', token: '{{data_atual_extenso}}' }
]

// --- Recorrência ---
const recorrenciaItems: VariableItem[] = [
  {
    label: 'Itens da composição da recorrência',
    token: '{{itens_recorrencia}}'
  },
  { label: 'Valor total da recorrência', token: '{{valor_total_recorrencia}}' },
  {
    label: 'Valor total da recorrência por extenso',
    token: '{{valor_total_recorrencia_extenso}}'
  },
  { label: 'Frequência da recorrência', token: '{{frequencia_recorrencia}}' },
  { label: 'Ciclo atual da recorrência', token: '{{ciclo_atual_recorrencia}}' },
  {
    label: 'Total de ciclos da recorrência',
    token: '{{total_ciclos_recorrencia}}'
  },
  {
    label: 'Total de ciclos da recorrência por extenso',
    token: '{{total_ciclos_recorrencia_extenso}}'
  },
  {
    label: 'Dia de vencimento da recorrência',
    token: '{{dia_vencimento_recorrencia}}'
  },
  {
    label: 'Forma de pagamento da recorrência',
    token: '{{forma_pagamento_recorrencia}}'
  },
  { label: 'Juros ao mês da recorrência (%)', token: '{{juros_recorrencia}}' },
  { label: 'Multa da recorrência (%)', token: '{{multa_recorrencia}}' },
  { label: 'Desconto da recorrência', token: '{{desconto_recorrencia}}' },
  {
    label: 'Bonificação da recorrência',
    token: '{{bonificacao_recorrencia}}'
  },
  {
    label: 'Data do último ciclo da recorrência',
    token: '{{data_ultimo_ciclo_recorrencia}}'
  }
]

// --- Cobrança ---
const cobrancaItems: VariableItem[] = [
  { label: 'Itens da composição da cobrança', token: '{{itens_da_cobranca}}' },
  { label: 'Valor total da cobrança', token: '{{valor_total_cobranca}}' },
  {
    label: 'Valor total da cobrança por extenso',
    token: '{{valor_total_cobranca_extenso}}'
  },
  {
    label: 'Forma de pagamento da cobrança',
    token: '{{forma_pagamento_cobranca}}'
  },
  { label: 'Juros ao mês da cobrança (%)', token: '{{juros_cobranca}}' },
  { label: 'Multa da cobrança (%)', token: '{{multa_cobranca}}' },
  { label: 'Desconto da cobrança', token: '{{desconto_cobranca}}' },
  { label: 'Bonificação da cobrança', token: '{{bonificacao_cobranca}}' },
  {
    label: 'Data de vencimento da cobrança',
    token: '{{data_vencimento_cobranca}}'
  },
  { label: 'Mês vencimento', token: '{{mes_vencimento}}/{{ano_vencimento}}' },
  {
    label: 'Mês anterior vencimento',
    token: '{{mes_anterior_vencimento}}/{{ano_anterior_vencimento}}'
  }
]

/**
 * Lista achatada de TODOS os tokens estáticos (empresa/cliente/gerais/
 * recorrência/cobrança), EXCLUINDO os dinâmicos de campo_extra. Usada como
 * allowlist na validação de submit. Inclui tanto a forma combinada dos pares
 * mês/ano de vencimento quanto cada peça individual, pois a extração de
 * `{{...}}` quebra o par no `/`.
 */
export const ALL_CHARGE_TOKENS: string[] = Array.from(
  new Set([
    // Empresa (inclui nome_fantasia sempre, mesmo que o menu esconda p/ PF)
    '{{empresa_razao_social}}',
    '{{empresa_nome_fantasia}}',
    '{{empresa_documento}}',
    '{{empresa_endereco}}',
    '{{empresa_email}}',
    '{{empresa_telefone}}',
    '{{empresa_data_fundacao}}',
    // Cliente
    ...clienteItems.map((item) => item.token),
    // Gerais
    ...geraisItems.map((item) => item.token),
    // Recorrência
    ...RECURRENCE_TOKENS,
    // Cobrança (tokens simples)
    '{{itens_da_cobranca}}',
    '{{valor_total_cobranca}}',
    '{{valor_total_cobranca_extenso}}',
    '{{forma_pagamento_cobranca}}',
    '{{juros_cobranca}}',
    '{{multa_cobranca}}',
    '{{desconto_cobranca}}',
    '{{bonificacao_cobranca}}',
    '{{data_vencimento_cobranca}}',
    // Pares de vencimento: forma combinada + peças individuais
    '{{mes_vencimento}}/{{ano_vencimento}}',
    '{{mes_vencimento}}',
    '{{ano_vencimento}}',
    '{{mes_anterior_vencimento}}/{{ano_anterior_vencimento}}',
    '{{mes_anterior_vencimento}}',
    '{{ano_anterior_vencimento}}'
  ])
)

/**
 * Monta as 6 categorias de variáveis com os tokens EXATOS. Empresa varia
 * labels por PJ/PF. "Campos extras" recebe os itens dinâmicos já mapeados.
 */
export const getChargeVariables = ({
  isCNPJ,
  customFields
}: {
  isCNPJ: boolean
  customFields: VariableItem[]
}): VariableCategory[] => [
  {
    key: 'empresa',
    label: 'Empresa',
    icon: 'storefront',
    items: empresaItems(isCNPJ)
  },
  { key: 'cliente', label: 'Cliente', icon: 'person', items: clienteItems },
  { key: 'gerais', label: 'Gerais', icon: 'event', items: geraisItems },
  {
    key: RECURRENCE_CATEGORY_KEY,
    label: 'Recorrência',
    icon: 'eventRepeat',
    items: recorrenciaItems
  },
  { key: 'cobranca', label: 'Cobrança', icon: 'receipt', items: cobrancaItems },
  {
    key: 'campos_extras',
    label: 'Campos extras',
    icon: 'listAlt',
    items: customFields
  }
]
